import React, { useMemo } from "react";
import { useEditorStore } from "@/stores/editor/editor-store";
import { Layout as CanvasIcon, Sparkles } from "lucide-react";
import { SketchAnnotationPanel } from "./SketchAnnotationPanel";
import { AIPropertiesChat } from "@/components/ai/AIPropertiesChat";
import { MapTemplateBrowserModal } from "./MapTemplateBrowserModal";
import { useMapTemplateBrowser } from "@/hooks/editor/useMapTemplateBrowser";
import { ComponentType } from "@/types";

interface PropertiesPanelProps {
  onSelectComponentTypeForSketch?: () => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ onSelectComponentTypeForSketch }) => {
  const {
    selectedComponents,
    canvasSelected,
    getSelectedComponents,
    selectedSketchId,
  } = useEditorStore();

  const selectedComponentObjects = useMemo(
    () => getSelectedComponents(),
    [getSelectedComponents, selectedComponents]
  );

  const singleSelectedComponent =
    selectedComponentObjects.length === 1 ? selectedComponentObjects[0] : null;

  const isMapComponent = singleSelectedComponent?.type === ComponentType.MAP_CHART;
  const mapTemplateBrowser = useMapTemplateBrowser(isMapComponent ? singleSelectedComponent : null);

  // Sketch selection — fundamentally different UI, separate return is fine
  if (selectedSketchId) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div
          className="flex-shrink-0"
          style={{
            padding: '16px',
            borderBottom: `1px solid hsl(var(--border))`,
            backgroundColor: `hsl(var(--muted))`,
          }}
        >
          <h3
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: `hsl(var(--foreground))`,
              margin: 0,
            }}
          >
            Sketch Properties
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ padding: '8px' }}>
          <SketchAnnotationPanel onSelectComponentType={onSelectComponentTypeForSketch} />
        </div>
      </div>
    );
  }

  // Unified return: AIPropertiesChat always at position 0 inside Fragment.
  // Prevents React unmount/remount when switching between canvas and component states.
  const isNoSelection = canvasSelected || selectedComponents.length === 0;

  return (
    <>
      <AIPropertiesChat
        component={isNoSelection ? null : singleSelectedComponent}
        isCanvas={isNoSelection}
        multipleComponents={!isNoSelection && selectedComponents.length > 1 ? selectedComponentObjects : undefined}
        className="h-full"
        onOpenMapTemplates={isMapComponent ? mapTemplateBrowser.openModal : undefined}
      />
      {isMapComponent && (
        <MapTemplateBrowserModal browser={mapTemplateBrowser} />
      )}
    </>
  );
};
